//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

//#region DevExtreme Imports

import DevExpress from 'devextreme'
import DataSource from 'devextreme/data/data_source'

//#endregion

//#region IMR Imports

import { IMRComponentInitializedArgs } from '@imr/controls.angularjs/components/base';
import { IMRTagBoxOptions, IIMRTagBox, IMRTagBoxDropDownDirection } from '@imr/controls.angularjs/components/tag-box';

//#endregion

import { OpOperator, OpDistributor } from '../../../../../../../../models/core'
import { NotificationType } from '../../../../../../../../models/util/email-notification-options'

import { LocalizationHelper } from '../../../../../../../../helpers'

import { OperatorNotificationsService } from '../../../../../../../../services/operator'

import { IMRBaseControlController } from '../../../../../../../common/base'
import { GridOptions, GridTypes, GridController } from '../../../../../../../common/grid'

import { OperatorDetailsNotificationsPageOptions, IOperatorDetailsNotificationsPage } from './operator-details.notifications.page.interfaces'
import { OperatorDetailsNotificationsPageState } from './operator-details.notifications.page.state'

export class OperatorDetailsNotificationsPageController
    extends
    IMRBaseControlController<IOperatorDetailsNotificationsPage, OperatorDetailsNotificationsPageOptions>
    implements
    IOperatorDetailsNotificationsPage,
    ng.IComponentController {

    //#region Members

    public isLoading: boolean
    public showAdditionalRecipients: boolean

    private allowColumnReordering: boolean
    private gridName: string
    private notificationType: NotificationType

    //#endregion


    //#region Ctor

    public constructor(private operatorNotificationsService: OperatorNotificationsService) {
        super()
    }

    //#endregion


    //#region Component

    protected initialize(options: OperatorDetailsNotificationsPageOptions): JQueryPromise<void> {
        this.isLoading = true
        this.setOptions()

        return $
            .when(
                this.initializeBottomToolbar(this.notificationType),
                this.initializeState(this.options.operator, this.notificationType),
                this.initializeGrid(this.notificationType)
            )
            .then(() => {
                this.isLoading = false
            })
    }

    //#endregion


    //#region IOperatorDetailsNotificationsPage Implementation

    public hasChanges(): boolean {
        return this.state.hasChanges()
    }

    public setOperator(operator: OpOperator): void {
        this.state.setOperator(operator)
    }

    public reset(): void {
        this.state.reset()
        this.notificationsGri.refresh()
        this.updateSubmitButton()
    }

    //#endregion


    //#region Grid

    public notificationsGridOptions: GridOptions
    private notificationsGri: GridController<any>

    private initializeGrid(notificationType: NotificationType): JQueryPromise<any> {
        const areNotificationsEditable: boolean = true

        this.notificationsGridOptions = {
            addCustomFilters: true,
            columnsDictionary: this.operatorNotificationsService.getNotificationsColumns(areNotificationsEditable),
            name: this.gridName,
            type: GridTypes.PAGINATED_SMALL,

            buttonsOptions: {
                enabled: true,
                isRefreshButonHidden: true,
            },

            editing: {
                mode: "cell",
                allowUpdating: areNotificationsEditable
            },

            allowColumnReordering: this.allowColumnReordering,

            onInitialized: (e: any, grid: GridController<any>) => {
                this.notificationsGri = grid
            },

            getDataSource: () => {
                return this.operatorNotificationsService.createNotificationGridDataSource(this.state.operator, notificationType)
            },

            onRowUpdating: (e: any) => {
                this.operatorNotificationsService.updateOptions(this.state.operator, notificationType, e.key, e.newData)
                this.state.updateStateOptions(this.state.operator)
                this.updateSubmitButton()
            }
        }

        return super.waitForOnInitialized(this.notificationsGridOptions)
    }

    //#endregion


    //#region State

    private state: OperatorDetailsNotificationsPageState

    private initializeState(operator: OpOperator, notificationType: NotificationType): void {
        this.state = OperatorDetailsNotificationsPageState.fromOperator(operator, notificationType)
    }

    //#endregion


    //#region Bottom Toolbar

    public additionalRecipientsTagBoxOptions: IMRTagBoxOptions<OpDistributor>
    public additionalRecipientsText: string
    public submitButtonOptions: DevExpress.ui.dxButtonOptions

    private submitButton: DevExpress.ui.dxButton
    private recipientsTagBox: IIMRTagBox<OpDistributor>

    private initializeBottomToolbar(notificationType: NotificationType): JQueryPromise<void> {
        const toInitialize: (void | JQueryPromise<void>)[] = [this.initializeSubmitButton()]

        if (this.showAdditionalRecipients) {
            toInitialize.push(
                this.initializeAdditionalRecipientsText(),
                this.initializeAdditionalRecipientsTagBox(notificationType)
            )
        }

        return $.when(...toInitialize)
    }

    private initializeAdditionalRecipientsText(): void {
        this.additionalRecipientsText = LocalizationHelper.getLocalizedText(LocalizationHelper.Text.AdditionalRecipients)
    }

    private initializeAdditionalRecipientsTagBox(notificationType: NotificationType): JQueryPromise<any> {
        return this.operatorNotificationsService
            .getAvailableNotificationDistributors()
            .then((distributors: OpDistributor[]) => {
                this.additionalRecipientsTagBoxOptions = {
                    dropDownButtonDirection: IMRTagBoxDropDownDirection.Up,
                    showDropDownButton: true,
                    showScrollBar: true,

                    innerOptions: {
                        dataSource: new DataSource({
                            paginate: true,
                            store: distributors
                        }),
                        displayExpr: 'toString',
                        maxDisplayedTags: 15,
                        searchEnabled: true,
                        selectAllMode: 'allPages',
                        showSelectionControls: true,
                        value: this.operatorNotificationsService.getIdDistributorsFromNotification(this.state.operator, notificationType),
                        valueExpr: 'getReferenceKey',

                        onValueChanged: (e: any) => {
                            this.operatorNotificationsService.updateNotificationDistributors(this.state.operator, this.recipientsTagBox.getSelectedItems(), notificationType)

                            const recipientIds = _.map(this.recipientsTagBox.getSelectedItems(), (recipient: OpDistributor) => recipient.idDistributor)
                            this.state.updateStateRecipients(recipientIds)

                            this.updateSubmitButton()
                        }
                    },

                    onInitialized: (e: IMRComponentInitializedArgs<IIMRTagBox<OpDistributor>>) => {
                        this.recipientsTagBox = e.component
                    },

                    onDisposed: () => {
                        this.recipientsTagBox = null
                    }
                }

                return super.waitForOnInitialized(this.additionalRecipientsTagBoxOptions)
            })
    }

    private initializeSubmitButton(): JQueryPromise<any> {
        this.submitButtonOptions = {
            disabled: true,
            text: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Apply),

            onInitialized: (e: any) => {
                this.submitButton = e.component
            },

            onClick: (e: any) => {
                super.callEvent(this.options.onSubmit, {
                    operator: this.state.operator
                })
            }
        }

        return super.waitForOnInitialized(this.submitButtonOptions)
    }

    private updateSubmitButton(): void {
        this.submitButton.option('disabled', !this.hasChanges())
    }

    //#endregion


    //#region Options

    private setDefaults(): void {
        this.allowColumnReordering = false
        this.gridName = null
        this.notificationType = null
    }

    private setOptions(): void {
        this.setDefaults()

        if (this.options != null) {
            if (this.options.allowColumnReordering != null) {
                this.allowColumnReordering = this.options.allowColumnReordering
            }

            if (this.options.gridName != null) {
                this.gridName = this.options.gridName
            }

            if (this.options.notificationType != null) {
                this.notificationType = this.options.notificationType
                this.showAdditionalRecipients = this.notificationType !== NotificationType.WorkOrder
            }
        }
    }

    //#endregion
}
